import { OTRSelect } from './OTRSelect';
import { OTRDateField } from './OTRDateField';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../../lib/utils';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '../../../components/ui/dialog';
import { DocumentUploadField } from './DocumentUploadField';

const EMPTY_FORM = { expenseHeadId: '', entryDate: '', amount: '', document: null };

export const AddExpenseModal = ({
  open,
  onOpenChange,
  expenseHeads = [],
  defaultHeadId = null,
  onAdd,
  isSaving,
  submitError = false,
  currencySymbol = '₹',
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, expenseHeadId: defaultHeadId ?? (expenseHeads[0]?.headId ?? '') });
      setErrors({});
    }
  }, [open, defaultHeadId, expenseHeads]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.expenseHeadId) next.expenseHeadId = 'Required';
    if (!form.entryDate) next.entryDate = 'Required';
    if (!form.amount || Number(form.amount) <= 0) next.amount = 'Enter a valid amount';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onAdd({
      expenseHeadId: Number(form.expenseHeadId),
      entryDate: form.entryDate,
      amount: Number(form.amount),
      document: form.document,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]',
            'w-[95vw] sm:w-[90vw] md:w-[440px] max-w-[440px]',
            'max-h-[90vh] overflow-hidden flex flex-col',
            'bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60',
            'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'font-sans'
          )}
        >
          <div className="flex items-start justify-between px-6 pt-6 pb-1 shrink-0">
            <div>
              <DialogTitle className="text-[20px] font-bold text-foreground tracking-tight">Add expense</DialogTitle>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">Select the expense head and enter its details.</p>
            </div>
            <DialogPrimitive.Close
              disabled={isSaving}
              className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition disabled:opacity-50 shrink-0"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Expense Type <span className="text-[var(--color-error)]"> *</span></label>
              <OTRSelect
                value={form.expenseHeadId}
                onChange={(v) => handleChange('expenseHeadId', String(v))}
                options={expenseHeads.map(head => ({ value: head.headId, label: head.headName }))}
                disabled={isSaving}
                hasError={!!errors.expenseHeadId}
              />
              {errors.expenseHeadId && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.expenseHeadId}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Date of Entry <span className="text-[var(--color-error)]"> *</span></label>
              <OTRDateField
                value={form.entryDate}
                onChange={(v) => handleChange('entryDate', v)}
                disabled={isSaving}
                hasError={!!errors.entryDate}
              />
              {errors.entryDate && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.entryDate}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Expense Amount <span className="text-[var(--color-error)]"> *</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-[var(--color-text-secondary)]">{currencySymbol}</span>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  disabled={isSaving}
                  placeholder="0.00"
                  className={`w-full h-11 pl-8 pr-4 text-sm font-bold rounded-xl border bg-white outline-none transition-all ${
                    errors.amount ? 'border-[var(--color-error)]' : 'border-[var(--color-border)] focus:ring-2 focus:ring-[var(--color-primary)]/20'
                  }`}
                />
              </div>
              {errors.amount && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.amount}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Attach Document</label>
              <DocumentUploadField
                value={form.document}
                onChange={(file) => handleChange('document', file)}
                isReadOnly={isSaving}
              />
              {errors.document && <span className="text-[10px] text-[var(--color-error)] font-bold ml-1">{errors.document}</span>}
            </div>

            {submitError && (
              <div className="p-3 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 text-sm text-[var(--color-error)] font-medium">
                {submitError}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--color-text-border)]/70 shrink-0">
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                disabled={isSaving}
                className="h-11 px-6 rounded-xl border border-[var(--color-text-border)] text-sm font-bold text-foreground hover:bg-[var(--color-workspace)]/50 transition disabled:opacity-50"
              >
                Cancel
              </button>
            </DialogPrimitive.Close>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className={cn(
                'h-11 px-6 rounded-xl text-white text-sm font-bold transition cursor-pointer flex items-center justify-center bg-[var(--color-primary)] hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]',
                isSaving && 'opacity-50 cursor-not-allowed hover:opacity-50 shadow-none'
              )}
            >
              {isSaving ? 'Adding...' : 'Add Expense'}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};